// =============================================================================
// FireISP 5.0 — Settings Page
// =============================================================================
// Admin-only page at /settings. Tabs:
//
//   1. Org Config       — key/value settings from GET/PUT /api/v1/settings
//   2. Alert Rules      — CRUD on alert rules via /api/v1/alerts/rules
//   3. Payment Gateways — CRUD on payment gateways via /api/v1/payment-gateways
//   4. Quotas           — per-tenant resource usage + limit management
//   5. Email            — per-organization outbound SMTP
//   6. Version          — what this instance is running, and whether a newer
//                         release exists. INSTALL OPERATOR ONLY (legacy
//                         users.role='admin'); hidden for everyone else,
//                         because the endpoint behind it 404s them.
//
// Message templates were promoted into their own page at /message-templates.
// =============================================================================

import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authedFetch } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SettingsTab = 'orgConfig' | 'alertRules' | 'paymentGateways' | 'quotas' | 'emailSettings' | 'version';

/**
 * One row of GET /settings. The backend stamps each entry with its scope and
 * whether THIS caller may edit it — `editable` is presentation truth, the
 * backend enforces the same rule on PUT (install keys are operator-only).
 */
interface Setting {
  key: string;
  value: string | null;
  description?: string;
  scope: 'org' | 'install';
  editable: boolean;
  details?: { enabled: boolean; endpoint: string | null; nasPort: number; clientPort: number };
}

/** Per-key input shapes for the known org settings; anything else is text. */
const SETTING_ENUM_OPTIONS: Record<string, string[]> = {
  mab_password_mode: ['auth_type_accept', 'cleartext'],
  wireguard_server_enabled: ['false', 'true'],
};
const SETTING_NUMBER_KEYS = ['pppoe_auth_failure_threshold'];

interface AlertRule {
  id: number;
  name: string;
  description: string | null;
  metric: string;
  operator: string;
  threshold: number;
  device_id: number | null;
  duration_minutes: number;
  severity: string;
  auto_create_outage: boolean;
  is_enabled: boolean;
  created_at: string;
}

interface PaymentGateway {
  id: number;
  provider: string;
  label: string | null;
  environment: string;
  public_key: string | null;
  webhook_secret: string | null;
  status: string;
  created_at: string;
}

interface EmailSettingsData {
  organization_id: number;
  enabled: boolean;
  smtp_host: string | null;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string | null;
  from_email: string | null;
  from_name: string | null;
  configured: boolean;
  has_password: boolean;
  last_test_at: string | null;
  last_test_status: 'success' | 'failed' | null;
  last_test_error: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = '/api/v1';
const METRICS = ['bandwidth_in', 'bandwidth_out', 'cpu', 'memory', 'signal', 'latency', 'uptime'];
const OPERATORS = ['>', '>=', '<', '<=', '='];
// Must match the alert_rules.severity DB enum (migration 134): info/warning/major/critical.
const SEVERITIES = ['critical', 'major', 'warning', 'info'];
const PROVIDERS = ['stripe', 'conekta', 'openpay', 'mercadopago', 'paypal', 'manual', 'other'];
const ENVIRONMENTS = ['sandbox', 'production'];

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await authedFetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) {
    // The API returns `error` as an OBJECT ({code, message}) on most routes and
    // as a bare string on a few older ones. Reading it as a string turned every
    // failure on this page into "[object Object]" — including the new
    // install-setting 403, whose message is the only thing telling an operator
    // what to do about it.
    const body = (await res.json().catch(() => ({}))) as {
      error?: string | { code?: string; message?: string };
      message?: string;
    };
    const detail = typeof body.error === 'string' ? body.error : body.error?.message;
    throw new Error(detail ?? body.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Org Config tab
// ---------------------------------------------------------------------------

function OrgConfigTab() {
  const { t } = useTranslation();
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saveError, setSaveError] = useState('');
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiFetch<{ data: Setting[] }>(`${API_BASE}/settings`),
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      apiFetch(`${API_BASE}/settings/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body: JSON.stringify({ value }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      setEditKey(null);
      setSaveError('');
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  if (isLoading) return <p style={sty.muted}>{t('settingsPage.loading')}</p>;
  if (error) return <p style={sty.errorText}>{t('settingsPage.loadError')}</p>;

  const settings = data?.data ?? [];
  const orgSettings = settings.filter(s => s.scope === 'org');
  const installSettings = settings.filter(s => s.scope === 'install');

  function startEdit(setting: Setting) {
    setEditKey(setting.key);
    setEditValue(setting.value ?? '');
    setSaveError('');
  }

  // Value editor matched to the key: known enums get a select, known numeric
  // keys a number input, everything else free text. The backend validates
  // regardless — this just makes the legal values discoverable.
  function valueEditor(setting: Setting) {
    const options = SETTING_ENUM_OPTIONS[setting.key];
    if (options) {
      return (
        <select style={sty.select} value={editValue} onChange={e => setEditValue(e.target.value)}>
          {options.map(o => (
            <option key={o} value={o}>
              {setting.key === 'wireguard_server_enabled'
                ? t(o === 'true' ? 'settingsPage.enabled' : 'settingsPage.disabled')
                : o}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        style={sty.input}
        type={SETTING_NUMBER_KEYS.includes(setting.key) ? 'number' : 'text'}
        min={SETTING_NUMBER_KEYS.includes(setting.key) ? 1 : undefined}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
      />
    );
  }

  function settingsTable(rows: Setting[]) {
    return (
      <table style={sty.table}>
        <thead>
          <tr>
            {[t('settingsPage.colKey'), t('settingsPage.colValue'), t('settingsPage.colDescription'), ''].map((h, i) => (
              <th key={i} style={sty.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(setting => (
            <tr key={setting.key}>
              <td style={sty.td}><code style={sty.code}>{setting.key}</code></td>
              <td style={{ ...sty.td, maxWidth: 260 }}>
                {editKey === setting.key ? valueEditor(setting) : (
                  <>
                    <span style={sty.valueCap}>
                      {setting.key === 'wireguard_server_enabled'
                        ? t(setting.value === 'true' ? 'settingsPage.enabled' : 'settingsPage.disabled')
                        : (setting.value || <em style={sty.muted}>—</em>)}
                    </span>
                    {setting.details && (
                      <div style={{ ...sty.muted, marginTop: 4, fontSize: '0.78rem' }}>
                        {t('settingsPage.wireguardPorts', {
                          endpoint: setting.details.endpoint || '—',
                          nasPort: setting.details.nasPort,
                          clientPort: setting.details.clientPort,
                        })}
                      </div>
                    )}
                  </>
                )}
              </td>
              <td style={{ ...sty.td, color: '#888', fontSize: '0.82rem' }}>{setting.description ?? ''}</td>
              <td style={sty.td}>
                {!setting.editable ? (
                  // Why it is not editable differs by scope: an install row
                  // needs the operator, an org row just needs settings.update.
                  // One label for both would tell a viewer something false.
                  <span style={sty.muted}>
                    {t(setting.scope === 'install' ? 'settingsPage.operatorOnly' : 'settingsPage.readOnly')}
                  </span>
                ) : editKey === setting.key ? (
                  <span style={sty.rowActions}>
                    <button style={sty.btnPrimary} disabled={updateMutation.isPending}
                      onClick={() => { setSaveError(''); updateMutation.mutate({ key: setting.key, value: editValue }); }}>
                      {t('settingsPage.save')}
                    </button>
                    <button style={sty.btnGhost} onClick={() => setEditKey(null)}>{t('settingsPage.cancel')}</button>
                  </span>
                ) : (
                  <button style={sty.btnGhost} onClick={() => startEdit(setting)}>{t('settingsPage.edit')}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div>
      <h3 style={sty.sectionTitle}>{t('settingsPage.orgSection')}</h3>
      {orgSettings.length === 0
        ? <p style={sty.muted}>{t('settingsPage.empty')}</p>
        : settingsTable(orgSettings)}

      <h3 style={{ ...sty.sectionTitle, marginTop: '2rem' }}>{t('settingsPage.installSection')}</h3>
      <p style={sty.muted}>{t('settingsPage.installNote')}</p>
      {installSettings.length === 0
        ? <p style={sty.muted}>{t('settingsPage.empty')}</p>
        : settingsTable(installSettings)}

      {saveError && <p style={sty.errorText}>{saveError}</p>}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Alert Rules tab
// ---------------------------------------------------------------------------

const EMPTY_RULE = {
  name: '', description: '', metric: 'bandwidth_in', operator: '>',
  threshold: '0', device_id: '', duration_minutes: '5', severity: 'major',
  auto_create_outage: false, is_enabled: true,
};

function AlertRulesTab() {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AlertRule | null>(null);
  const [form, setForm] = useState({ ...EMPTY_RULE });
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () =>
      apiFetch<{ data: AlertRule[]; meta: { total: number } }>(`${API_BASE}/alerts/rules?limit=100`),
  });

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      editing
        ? apiFetch(`${API_BASE}/alerts/rules/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : apiFetch(`${API_BASE}/alerts/rules`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['alert-rules'] }); closeModal(); },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`${API_BASE}/alerts/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['alert-rules'] }); setDeleteId(null); },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_enabled }: { id: number; is_enabled: boolean }) =>
      apiFetch(`${API_BASE}/alerts/rules/${id}`, { method: 'PUT', body: JSON.stringify({ is_enabled }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alert-rules'] }),
  });

  function openNew() { setEditing(null); setForm({ ...EMPTY_RULE }); setFormError(''); setShowModal(true); }
  function openEdit(r: AlertRule) {
    setEditing(r);
    setForm({
      name: r.name, description: r.description ?? '', metric: r.metric,
      operator: r.operator, threshold: String(r.threshold),
      device_id: r.device_id ? String(r.device_id) : '',
      duration_minutes: String(r.duration_minutes),
      severity: r.severity, auto_create_outage: r.auto_create_outage, is_enabled: r.is_enabled,
    });
    setFormError(''); setShowModal(true);
  }
  function closeModal() { setShowModal(false); setEditing(null); }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    const threshold = parseFloat(form.threshold);
    if (isNaN(threshold)) { setFormError('Threshold must be a number'); return; }
    saveMutation.mutate({
      name: form.name, description: form.description || undefined,
      metric: form.metric, operator: form.operator, threshold,
      device_id: form.device_id ? parseInt(form.device_id, 10) : undefined,
      duration_minutes: parseInt(form.duration_minutes, 10) || 5,
      severity: form.severity, auto_create_outage: form.auto_create_outage,
      is_enabled: form.is_enabled,
    });
  }

  const rules = data?.data ?? [];

  return (
    <div>
      <div style={sty.tabBar}>
        <h3 style={sty.sectionTitle}>Alert Rules</h3>
        <button style={sty.btnPrimary} onClick={openNew}>+ New Rule</button>
      </div>

      {isLoading && <p style={sty.muted}>Loading rules…</p>}
      {error && <p style={sty.errorText}>Failed to load alert rules.</p>}
      {!isLoading && rules.length === 0 && <p style={sty.muted}>No alert rules defined.</p>}

      {rules.length > 0 && (
        <table style={sty.table}>
          <thead>
            <tr>{['Rule', 'Metric', 'Condition', 'Severity', 'Enabled', ''].map(h => <th key={h} style={sty.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.id}>
                <td style={sty.td}>
                  <div style={{ fontWeight: 500 }}>{r.name}</div>
                  {r.description && <div style={{ fontSize: '0.8rem', color: '#888' }}>{r.description}</div>}
                </td>
                <td style={sty.td}><code style={sty.code}>{r.metric}</code></td>
                <td style={sty.td}>{r.operator} {r.threshold} for {r.duration_minutes}m</td>
                <td style={sty.td}><span style={severityBadge(r.severity)}>{r.severity}</span></td>
                <td style={sty.td}>
                  <button
                    style={r.is_enabled ? sty.btnPrimary : sty.btnGhost}
                    onClick={() => toggleMutation.mutate({ id: r.id, is_enabled: !r.is_enabled })}
                    disabled={toggleMutation.isPending}
                  >
                    {r.is_enabled ? 'On' : 'Off'}
                  </button>
                </td>
                <td style={sty.td}>
                  <span style={sty.rowActions}>
                    <button style={sty.btnGhost} onClick={() => openEdit(r)}>Edit</button>
                    <button style={sty.btnDanger} onClick={() => setDeleteId(r.id)}>Delete</button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <Modal title={editing ? 'Edit Alert Rule' : 'New Alert Rule'} onClose={closeModal}>
          <form onSubmit={handleSubmit} style={sty.form}>
            <label style={sty.label}>Name *
              <input style={sty.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </label>
            <label style={sty.label}>Description
              <input style={sty.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </label>
            <div style={sty.row2}>
              <label style={sty.label}>Metric *
                <select style={sty.select} value={form.metric} onChange={e => setForm(f => ({ ...f, metric: e.target.value }))}>
                  {METRICS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label style={sty.label}>Operator
                <select style={sty.select} value={form.operator} onChange={e => setForm(f => ({ ...f, operator: e.target.value }))}>
                  {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            </div>
            <div style={sty.row2}>
              <label style={sty.label}>Threshold *
                <input style={sty.input} type="number" step="any" value={form.threshold}
                  onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))} required />
              </label>
              <label style={sty.label}>Duration (min)
                <input style={sty.input} type="number" min="1" value={form.duration_minutes}
                  onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} />
              </label>
            </div>
            <div style={sty.row2}>
              <label style={sty.label}>Severity
                <select style={sty.select} value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}>
                  {SEVERITIES.map(sv => <option key={sv} value={sv}>{sv}</option>)}
                </select>
              </label>
              <label style={sty.label}>Device ID (optional)
                <input style={sty.input} type="number" value={form.device_id} placeholder="leave blank for all"
                  onChange={e => setForm(f => ({ ...f, device_id: e.target.value }))} />
              </label>
            </div>
            <div style={sty.checkRow}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.auto_create_outage}
                  onChange={e => setForm(f => ({ ...f, auto_create_outage: e.target.checked }))} />
                Auto-create outage when triggered
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_enabled}
                  onChange={e => setForm(f => ({ ...f, is_enabled: e.target.checked }))} />
                Enabled
              </label>
            </div>
            {formError && <p style={sty.errorText}>{formError}</p>}
            <div style={sty.modalFooter}>
              <button type="button" style={sty.btnGhost} onClick={closeModal}>Cancel</button>
              <button type="submit" style={sty.btnPrimary} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {deleteId !== null && (
        <ConfirmDialog
          message="Delete this alert rule?"
          onConfirm={() => deleteMutation.mutate(deleteId!)}
          onCancel={() => setDeleteId(null)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment Gateways tab
// ---------------------------------------------------------------------------

const EMPTY_GW = {
  provider: 'stripe', label: '', environment: 'sandbox',
  public_key: '', secret_key_encrypted: '', webhook_secret: '', status: 'active',
};

function PaymentGatewaysTab() {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PaymentGateway | null>(null);
  const [form, setForm] = useState({ ...EMPTY_GW });
  const [formError, setFormError] = useState('');
  const [copied, setCopied] = useState<null | 'ok' | 'fail'>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['payment-gateways'],
    queryFn: () =>
      apiFetch<{ data: PaymentGateway[]; meta: { total: number } }>(`${API_BASE}/payment-gateways?limit=100`),
  });

  const saveMutation = useMutation({
    mutationFn: (body: typeof form) =>
      editing
        ? apiFetch(`${API_BASE}/payment-gateways/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : apiFetch(`${API_BASE}/payment-gateways`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payment-gateways'] }); closeModal(); },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`${API_BASE}/payment-gateways/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payment-gateways'] }); setDeleteId(null); },
  });

  function openNew() { setEditing(null); setForm({ ...EMPTY_GW }); setFormError(''); setShowModal(true); }
  function openEdit(gw: PaymentGateway) {
    setEditing(gw);
    setForm({
      provider: gw.provider, label: gw.label ?? '', environment: gw.environment,
      public_key: gw.public_key ?? '', secret_key_encrypted: '',
      webhook_secret: gw.webhook_secret ?? '', status: gw.status,
    });
    setFormError(''); setShowModal(true);
  }
  function closeModal() { setShowModal(false); setEditing(null); }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    saveMutation.mutate(form);
  }

  const gateways = data?.data ?? [];

  return (
    <div>
      <div style={sty.tabBar}>
        <h3 style={sty.sectionTitle}>Payment Gateways</h3>
        <button style={sty.btnPrimary} onClick={openNew}>+ Add Gateway</button>
      </div>

      {isLoading && <p style={sty.muted}>Loading gateways…</p>}
      {error && <p style={sty.errorText}>Failed to load payment gateways.</p>}
      {!isLoading && gateways.length === 0 && <p style={sty.muted}>No payment gateways configured.</p>}

      {gateways.length > 0 && (
        <table style={sty.table}>
          <thead>
            <tr>{['Provider', 'Label', 'Environment', 'Status', ''].map(h => <th key={h} style={sty.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {gateways.map(gw => (
              <tr key={gw.id}>
                <td style={sty.td}><span style={channelBadge(gw.provider)}>{gw.provider}</span></td>
                <td style={sty.td}>{gw.label ?? <em style={sty.muted}>—</em>}</td>
                <td style={sty.td}>{gw.environment}</td>
                <td style={sty.td}><span style={statusBadge(gw.status)}>{gw.status}</span></td>
                <td style={sty.td}>
                  <span style={sty.rowActions}>
                    <button style={sty.btnGhost} onClick={() => openEdit(gw)}>Edit</button>
                    <button style={sty.btnDanger} onClick={() => setDeleteId(gw.id)}>Delete</button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <Modal title={editing ? 'Edit Gateway' : 'Add Payment Gateway'} onClose={closeModal}>
          <form onSubmit={handleSubmit} style={sty.form}>
            <div style={sty.row2}>
              <label style={sty.label}>Provider *
                <select style={sty.select} value={form.provider} disabled={!!editing}
                  onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}>
                  {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                {editing && <span style={sty.hint}>Provider is fixed for an existing gateway (it drives webhook/charge routing) — create a new gateway to switch.</span>}
              </label>
              <label style={sty.label}>Label
                <input style={sty.input} value={form.label} placeholder="e.g. Stripe MX"
                  onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
              </label>
            </div>
            <div style={sty.row2}>
              <label style={sty.label}>Environment
                <select style={sty.select} value={form.environment} onChange={e => setForm(f => ({ ...f, environment: e.target.value }))}>
                  {ENVIRONMENTS.map(env => <option key={env} value={env}>{env}</option>)}
                </select>
              </label>
              <label style={sty.label}>Status
                <select style={sty.select} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </label>
            </div>
            <label style={sty.label}>Public Key
              <input style={sty.input} value={form.public_key} placeholder="pk_live_…"
                onChange={e => setForm(f => ({ ...f, public_key: e.target.value }))} />
            </label>
            <label style={sty.label}>
              Secret Key {editing && <span style={sty.hint}>(leave blank to keep existing)</span>}
              <input style={sty.input} type="password" value={form.secret_key_encrypted}
                placeholder={editing ? '••••••••' : 'sk_live_…'}
                onChange={e => setForm(f => ({ ...f, secret_key_encrypted: e.target.value }))} />
            </label>
            <label style={sty.label}>Webhook Secret
              <input style={sty.input} value={form.webhook_secret} placeholder="whsec_…"
                onChange={e => setForm(f => ({ ...f, webhook_secret: e.target.value }))} />
            </label>
            {editing && (editing.provider === 'stripe' || editing.provider === 'conekta') && (() => {
              const webhookUrl = `${window.location.origin}/api/v1/payment-webhooks/${editing.provider}/${editing.id}`;
              return (
                <div style={sty.label}>
                  <span>Webhook URL <span style={sty.hint}>(paste into your {editing.provider} dashboard)</span></span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <code style={{ ...sty.code, flex: 1, overflowX: 'auto', whiteSpace: 'nowrap' }}>{webhookUrl}</code>
                    <button type="button" style={sty.btnGhost} onClick={async () => {
                      try {
                        if (!navigator.clipboard) throw new Error('clipboard unavailable');
                        await navigator.clipboard.writeText(webhookUrl);
                        setCopied('ok');
                      } catch {
                        setCopied('fail');  // e.g. plain-HTTP admin panel — the URL above is selectable
                      }
                      setTimeout(() => setCopied(null), 2000);
                    }}>{copied === 'ok' ? 'Copied!' : copied === 'fail' ? 'Select it ↑' : 'Copy'}</button>
                  </div>
                  <span style={sty.hint}>Set this as the webhook endpoint in {editing.provider}, then paste the signing secret it returns into “Webhook Secret” above. Until a secret is saved here, incoming webhooks are rejected (503) — payments won’t auto-reconcile.</span>
                </div>
              );
            })()}
            {formError && <p style={sty.errorText}>{formError}</p>}
            <div style={sty.modalFooter}>
              <button type="button" style={sty.btnGhost} onClick={closeModal}>Cancel</button>
              <button type="submit" style={sty.btnPrimary} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {deleteId !== null && (
        <ConfirmDialog
          message="Delete this payment gateway? Existing transactions linked to it will be preserved."
          onConfirm={() => deleteMutation.mutate(deleteId!)}
          onCancel={() => setDeleteId(null)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helper components
// ---------------------------------------------------------------------------

interface ModalProps { title: string; onClose: () => void; children: React.ReactNode; }
function Modal({ title, onClose, children }: ModalProps) {
  return (
    <div style={sty.overlay}>
      <div style={sty.modal}>
        <div style={sty.modalHeader}>
          <span style={{ fontWeight: 600 }}>{title}</span>
          <button style={sty.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={sty.modalBody}>{children}</div>
      </div>
    </div>
  );
}

interface ConfirmProps { message: string; onConfirm: () => void; onCancel: () => void; loading: boolean; }
function ConfirmDialog({ message, onConfirm, onCancel, loading }: ConfirmProps) {
  return (
    <div style={sty.overlay}>
      <div style={{ ...sty.modal, maxWidth: 400 }}>
        <div style={sty.modalBody}>
          <p style={{ marginTop: 0 }}>{message}</p>
          <div style={sty.modalFooter}>
            <button style={sty.btnGhost} onClick={onCancel} disabled={loading}>Cancel</button>
            <button style={sty.btnDanger} onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

function channelBadge(label: string) {
  const colours: Record<string, string> = {
    email: '#3b82f6', sms: '#8b5cf6', whatsapp: '#22c55e', push: '#f59e0b',
    stripe: '#635bff', conekta: '#e74c3c', openpay: '#2ecc71', paypal: '#003087',
    mercadopago: '#009ee3', manual: '#888', other: '#888',
  };
  return { ...sty.badge, background: colours[label] ?? '#888' };
}

function severityBadge(level: string) {
  const colours: Record<string, string> = {
    critical: '#dc2626', major: '#ea580c', warning: '#ca8a04', info: '#0284c7',
  };
  return {
    padding: '2px 8px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600,
    background: colours[level] ?? '#888', color: '#fff',
  };
}

function statusBadge(st: string) {
  return {
    padding: '2px 8px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600,
    background: st === 'active' ? '#16a34a' : '#888', color: '#fff',
  };
}

// ---------------------------------------------------------------------------
// Quotas Tab
// ---------------------------------------------------------------------------

interface QuotaLimits {
  max_clients: number | null;
  max_devices: number | null;
  max_storage_mb: number | null;
  max_scheduled_tasks: number | null;
}

interface QuotaUsage {
  clients: number;
  devices: number;
  storage_mb: number;
  scheduled_tasks: number;
}

interface QuotaData {
  limits: QuotaLimits;
  usage: QuotaUsage;
}

function QuotaBar({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const isUnlimited = limit === null;
  const pct = (isUnlimited || limit === 0) ? 100 : Math.min(100, Math.round((used / limit!) * 100));
  const color = pct >= 95 ? '#dc2626' : pct >= 80 ? '#d97706' : '#16a34a';
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.875rem' }}>
        <span style={{ fontWeight: 500 }}>{label}</span>
        <span style={{ color: '#555' }}>
          {used.toLocaleString()} / {isUnlimited ? '∞' : limit!.toLocaleString()}
          {!isUnlimited && <span style={{ color: pct >= 95 ? '#dc2626' : '#888', marginLeft: 6 }}>({pct}%)</span>}
        </span>
      </div>
      {!isUnlimited && (
        <div style={{ background: '#f3f4f6', borderRadius: 6, height: 8, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 6, transition: 'width .3s' }} />
        </div>
      )}
    </div>
  );
}

function QuotasTab() {
  const { user } = useAuth();
  const orgId = user?.organization_id;
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<{ data: QuotaData }>({
    queryKey: ['org-quota', orgId],
    queryFn: () => apiFetch<{ data: QuotaData }>(`/api/v1/organizations/${orgId}/quota`),
    enabled: !!orgId,
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Record<keyof QuotaLimits, string>>>({});

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, number | null>) =>
      apiFetch<{ data: QuotaData }>(`/api/v1/organizations/${orgId}/quota`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-quota', orgId] });
      setEditing(false);
    },
  });

  if (isLoading) return <p style={sty.muted}>Loading quota…</p>;
  if (error || !data) return <p style={{ color: '#dc2626', fontSize: '0.875rem' }}>Failed to load quota.</p>;

  const { limits, usage } = data.data;

  function startEdit() {
    setForm({
      max_clients:         limits.max_clients         === null ? '' : String(limits.max_clients),
      max_devices:         limits.max_devices         === null ? '' : String(limits.max_devices),
      max_storage_mb:      limits.max_storage_mb      === null ? '' : String(limits.max_storage_mb),
      max_scheduled_tasks: limits.max_scheduled_tasks === null ? '' : String(limits.max_scheduled_tasks),
    });
    setEditing(true);
  }

  function handleSave(e: FormEvent) {
    e.preventDefault();
    const body: Record<string, number | null> = {};
    for (const [k, v] of Object.entries(form)) {
      body[k] = v === '' || v === undefined ? null : Number(v);
    }
    saveMutation.mutate(body);
  }

  return (
    <div>
      <div style={sty.tabBar}>
        <h3 style={sty.sectionTitle}>Resource Quotas</h3>
        {!editing && (
          <button style={sty.btnPrimary} onClick={startEdit}>✏️ Edit Limits</button>
        )}
      </div>

      {!editing ? (
        <>
          <p style={{ fontSize: '0.85rem', color: '#555', marginTop: 0, marginBottom: '1.5rem' }}>
            Current usage versus configured limits. <strong>∞</strong> means unlimited.
          </p>
          <QuotaBar label="Clients"         used={usage.clients}         limit={limits.max_clients} />
          <QuotaBar label="Devices"         used={usage.devices}         limit={limits.max_devices} />
          <QuotaBar label="Storage (MB)"    used={usage.storage_mb}      limit={limits.max_storage_mb} />
          <QuotaBar label="Scheduled Tasks" used={usage.scheduled_tasks} limit={limits.max_scheduled_tasks} />
        </>
      ) : (
        <form onSubmit={handleSave} style={sty.form}>
          <p style={{ fontSize: '0.85rem', color: '#555', margin: '0 0 0.75rem' }}>
            Leave a field blank to set it as <strong>unlimited</strong>.
          </p>
          {(
            [
              ['max_clients',         'Max Clients'],
              ['max_devices',         'Max Devices'],
              ['max_storage_mb',      'Max Storage (MB)'],
              ['max_scheduled_tasks', 'Max Scheduled Tasks'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} style={sty.label}>
              {label}
              <input
                style={sty.input}
                type="number"
                min={0}
                placeholder="unlimited"
                value={form[key] ?? ''}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              />
            </label>
          ))}
          {saveMutation.error && (
            <p style={sty.errorText}>{(saveMutation.error as Error).message}</p>
          )}
          <div style={sty.modalFooter}>
            <button type="button" style={sty.btnGhost} onClick={() => setEditing(false)}>Cancel</button>
            <button type="submit" style={sty.btnPrimary} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save Limits'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email Settings tab — per-org outbound SMTP configuration (migration 386)
// ---------------------------------------------------------------------------

const EMPTY_EMAIL_FORM = {
  enabled: true,
  smtp_host: '',
  smtp_port: '587',
  smtp_secure: false,
  smtp_user: '',
  smtp_password: '',
  from_email: '',
  from_name: '',
};

function EmailSettingsTab({ userId, organizationId }: { userId: number | null; organizationId: number | null }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...EMPTY_EMAIL_FORM });
  const [clearStoredPassword, setClearStoredPassword] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const emailSettingsQueryKey = ['email-settings', userId, organizationId] as const;
  const { data, isLoading, error } = useQuery({
    queryKey: emailSettingsQueryKey,
    queryFn: () => apiFetch<{ data: EmailSettingsData }>(`${API_BASE}/email-settings`),
    enabled: userId !== null,
  });

  useEffect(() => {
    const d = data?.data;
    if (d) {
      setForm({
        enabled: d.enabled,
        smtp_host: d.smtp_host ?? '',
        smtp_port: String(d.smtp_port ?? 587),
        smtp_secure: d.smtp_secure,
        smtp_user: d.smtp_user ?? '',
        smtp_password: '',
        from_email: d.from_email ?? '',
        from_name: d.from_name ?? '',
      });
      setClearStoredPassword(false);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ data: EmailSettingsData }>(`${API_BASE}/email-settings`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailSettingsQueryKey });
      setForm(current => ({ ...current, smtp_password: '' }));
      setClearStoredPassword(false);
      setMsg({ type: 'success', text: t('emailSettings.saved') });
      setTimeout(() => setMsg(null), 3000);
    },
    onError: (err: Error) => setMsg({ type: 'error', text: err.message || t('emailSettings.saveError') }),
  });

  const testMutation = useMutation({
    mutationFn: (to: string) =>
      apiFetch<{ data: { success: boolean; error?: string } }>(`${API_BASE}/email-settings/test`, {
        method: 'POST',
        body: JSON.stringify({ to }),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: emailSettingsQueryKey });
      if (res.data.success) {
        setTestResult({ type: 'success', text: t('emailSettings.testSuccess') });
      } else {
        setTestResult({ type: 'error', text: t('emailSettings.testFailed', { error: res.data.error ?? '' }) });
      }
    },
    onError: (err: Error) => setTestResult({ type: 'error', text: t('emailSettings.testFailed', { error: err.message }) }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (connectionChangeNeedsPasswordAction) {
      setMsg({ type: 'error', text: t('emailSettings.connectionChangeRequiresPassword') });
      return;
    }
    const body: Record<string, unknown> = {
      enabled: form.enabled,
      smtp_host: form.smtp_host || null,
      smtp_port: form.smtp_port ? Number(form.smtp_port) : undefined,
      smtp_secure: form.smtp_secure,
      smtp_user: form.smtp_user || null,
      from_email: form.from_email || null,
      from_name: form.from_name || null,
    };
    // Write-only password field: only send when the operator actually typed
    // something. Omitted -> backend keeps the existing encrypted value.
    if (clearStoredPassword) body.smtp_password = '';
    else if (form.smtp_password) body.smtp_password = form.smtp_password;
    saveMutation.mutate(body);
  }

  function handleTestSubmit(e: FormEvent) {
    e.preventDefault();
    setTestResult(null);
    if (!testTo.trim()) return;
    testMutation.mutate(testTo.trim());
  }

  const configured = data?.data?.configured ?? false;
  const hasPassword = data?.data?.has_password ?? false;
  const saved = data?.data;
  const normalizedHost = (value: string | null | undefined) => (value ?? '').trim().toLowerCase().replace(/\.+$/, '');
  const connectionIdentityChanged = Boolean(hasPassword && saved && (
    normalizedHost(form.smtp_host) !== normalizedHost(saved.smtp_host)
    || Number(form.smtp_port || 587) !== Number(saved.smtp_port || 587)
    || form.smtp_secure !== saved.smtp_secure
    || form.smtp_user.trim() !== (saved.smtp_user ?? '').trim()
  ));
  const connectionChangeNeedsPasswordAction = connectionIdentityChanged
    && !form.smtp_password
    && !clearStoredPassword;
  const lastTestAt = data?.data?.last_test_at;
  const lastTestStatus = data?.data?.last_test_status;

  return (
    <div>
      <h3 style={sty.sectionTitle}>{t('emailSettings.title')}</h3>

      {isLoading && <p style={sty.muted}>{t('common.loading')}</p>}
      {error && <p style={sty.errorText}>{t('emailSettings.loadError')}</p>}

      <p style={{ fontSize: '0.85rem', color: configured ? '#16a34a' : '#888', marginTop: 0 }}>
        {configured ? `✓ ${t('emailSettings.configuredLabel')}` : t('emailSettings.notConfigured')}
        {lastTestAt && (
          <span style={{ marginLeft: 10, color: 'var(--text-faint)' }}>
            {t('emailSettings.lastTested', { date: new Date(lastTestAt).toLocaleString(), status: lastTestStatus })}
          </span>
        )}
      </p>

      <form onSubmit={handleSubmit} style={{ ...sty.form, maxWidth: 480 }}>
        {msg && (
          <div style={{
            padding: '10px 14px', borderRadius: 6,
            background: msg.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: msg.type === 'success' ? '#065f46' : '#991b1b', fontSize: 14,
          }}>
            {msg.text}
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
          <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
          {t('emailSettings.enabled')}
        </label>
        <p style={{ fontSize: '0.78rem', color: 'var(--text-faint)', margin: '-6px 0 0' }}>{t('emailSettings.enabledHint')}</p>

        <label style={sty.label}>{t('emailSettings.smtpHost')}
          <input style={sty.input} value={form.smtp_host} placeholder="smtp.example.com"
            onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))} />
        </label>

        <div style={sty.row2}>
          <label style={sty.label}>{t('emailSettings.smtpPort')}
            <input style={sty.input} type="number" min={1} max={65535} value={form.smtp_port}
              onChange={e => setForm(f => ({ ...f, smtp_port: e.target.value }))} />
          </label>
          <label style={{ ...sty.label, justifyContent: 'flex-end', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.smtp_secure} onChange={e => setForm(f => ({ ...f, smtp_secure: e.target.checked }))} />
            {t('emailSettings.smtpSecure')}
          </label>
        </div>
        <p style={{ fontSize: '0.78rem', color: 'var(--text-faint)', margin: '-6px 0 0' }}>
          {t('emailSettings.smtpSecureHint')}
        </p>

        <label style={sty.label}>{t('emailSettings.smtpUser')}
          <input style={sty.input} value={form.smtp_user} autoComplete="off"
            onChange={e => setForm(f => ({ ...f, smtp_user: e.target.value }))} />
        </label>

        <label style={sty.label}>
          {t('emailSettings.smtpPassword')} <span style={sty.hint}>({t('emailSettings.smtpPasswordHint')})</span>
          <input style={sty.input} type="password" autoComplete="new-password"
            value={form.smtp_password} placeholder={hasPassword ? '••••••••' : ''}
            disabled={clearStoredPassword}
            onChange={e => {
              setClearStoredPassword(false);
              setForm(f => ({ ...f, smtp_password: e.target.value }));
            }} />
        </label>

        {hasPassword && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.82rem' }}>
            <input
              type="checkbox"
              checked={clearStoredPassword}
              onChange={e => {
                setClearStoredPassword(e.target.checked);
                if (e.target.checked) setForm(f => ({ ...f, smtp_password: '' }));
              }}
            />
            {t('emailSettings.clearStoredPassword')}
          </label>
        )}
        {clearStoredPassword && (
          <p style={{ fontSize: '0.78rem', color: '#92400e', margin: '-6px 0 0' }}>
            {t('emailSettings.clearStoredPasswordHint')}
          </p>
        )}
        {connectionChangeNeedsPasswordAction && (
          <p role="alert" style={{ fontSize: '0.8rem', color: '#991b1b', margin: '-6px 0 0' }}>
            {t('emailSettings.connectionChangeRequiresPassword')}
          </p>
        )}

        <div style={sty.row2}>
          <label style={sty.label}>{t('emailSettings.fromEmail')}
            <input style={sty.input} type="email" value={form.from_email}
              onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))} />
          </label>
          <label style={sty.label}>{t('emailSettings.fromName')}
            <input style={sty.input} value={form.from_name}
              onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))} />
          </label>
        </div>

        <div>
          <button type="submit" style={sty.btnPrimary} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>

      <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)', maxWidth: 480 }}>
        <form onSubmit={handleTestSubmit} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <label style={{ ...sty.label, flex: 1 }}>{t('emailSettings.testEmailPrompt')}
            <input style={sty.input} type="email" required value={testTo}
              onChange={e => setTestTo(e.target.value)} placeholder="you@example.com" />
          </label>
          <button type="submit" style={sty.btnGhost} disabled={testMutation.isPending}>
            {testMutation.isPending ? t('common.saving') : t('emailSettings.testEmail')}
          </button>
        </form>
        {testResult && (
          <p style={{ fontSize: '0.85rem', marginTop: 8, color: testResult.type === 'success' ? '#16a34a' : '#dc2626' }}>
            {testResult.text}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Version tab — what am I running, and is there anything newer?
// ---------------------------------------------------------------------------
// Before this, the answer lived nowhere in the product: package.json carries a
// static "5.0.0", and the update banner only appears when an update IS
// available AND the check is switched on — so an operator asking "what version
// is this?" or "is the check even working?" had nothing to look at.
//
// Deliberately READ-ONLY. The opt-in is an env var precisely because the
// `settings` table is writable by any org admin, so offering a toggle here
// would either not work or would reintroduce that hole.

interface DeployRequest {
  id: number;
  status: 'pending' | 'running' | 'succeeded' | 'failed';
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
  exit_code: number | null;
  output_tail: string | null;
}

interface DeployState {
  request: DeployRequest | null;
  agent_alive: boolean;
  agent_last_seen_at: string | null;
  agent_hostname: string | null;
}

interface SystemVersion {
  running_sha: string | null;
  latest_sha: string | null;
  update_available: boolean;
  check_enabled: boolean;
  checked_at: string | null;
  /** A refresh is running behind this response; poll again shortly. */
  refreshing?: boolean;
}

function VersionTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    // UpdateAvailableBanner shares this queryKey and caches the UNWRAPPED
    // SystemVersion — this queryFn must unwrap too. When they disagreed, a
    // fresh banner-shaped cache entry made `data.data` undefined here and the
    // tab rendered an empty card until the entry went stale.
    queryKey: ['system-version'],
    queryFn: () => apiFetch<{ data: SystemVersion }>(`${API_BASE}/system/version`)
      .then((r) => r.data),
    retry: false,
    // The server answers instantly with a possibly-stale value rather than
    // blocking on api.github.com. When it says a refresh is in flight, look
    // again shortly so the fresh answer arrives without the operator having to
    // do anything — otherwise "fast" would just mean "stale".
    refetchInterval: (q) => (
      (q.state.data as SystemVersion | undefined)?.refreshing ? 1500 : false
    ),
  });

  // Polled only while something is in flight — an idle Settings page has no
  // business hitting the server every few seconds forever.
  const [deployError, setDeployError] = useState<string | null>(null);
  const { data: deployData } = useQuery({
    queryKey: ['system-deploy'],
    queryFn: () => apiFetch<{ data: DeployState }>(`${API_BASE}/system/deploy`),
    retry: false,
    refetchInterval: (q) => {
      const st = (q.state.data as { data: DeployState } | undefined)?.data?.request?.status;
      return st === 'pending' || st === 'running' ? 5000 : false;
    },
  });

  // Forces a fresh look upstream. The answer is otherwise at most 15 minutes
  // old, which is fine passively but not when someone has deliberately come
  // here to ask.
  const checkMutation = useMutation({
    mutationFn: () => apiFetch(`${API_BASE}/system/version/check`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system-version'] }),
  });

  const deployMutation = useMutation({
    mutationFn: () => apiFetch(`${API_BASE}/system/deploy`, { method: 'POST' }),
    onSuccess: () => { setDeployError(null); qc.invalidateQueries({ queryKey: ['system-deploy'] }); },
    onError: (e: Error) => setDeployError(e.message),
  });

  if (isLoading) return <p style={sty.muted}>{t('version.loading')}</p>;
  if (error) return <p style={sty.errorText}>{(error as Error).message}</p>;

  const v = data;
  if (!v) return null;

  const dep = deployData?.data;
  const busy = dep?.request?.status === 'pending' || dep?.request?.status === 'running';

  const short = (sha: string | null) => (sha ? sha.slice(0, 7) : '—');

  return (
    <div>
      <h3 style={sty.sectionTitle}>{t('version.title')}</h3>

      <dl style={sty.verList}>
        <dt style={sty.verKey}>{t('version.running')}</dt>
        <dd style={sty.verVal}>
          {v.running_sha
            ? <code style={sty.code}>{short(v.running_sha)}</code>
            : <span style={sty.muted}>{t('version.unknownBuild')}</span>}
        </dd>

        <dt style={sty.verKey}>{t('version.checkState')}</dt>
        <dd style={sty.verVal}>
          {v.check_enabled ? t('version.checkOn') : t('version.checkOff')}
        </dd>

        {v.check_enabled && (
          <>
            <dt style={sty.verKey}>{t('version.latest')}</dt>
            <dd style={sty.verVal}>
              {v.latest_sha
                ? <code style={sty.code}>{short(v.latest_sha)}</code>
                : <span style={sty.muted}>{t('version.unreachable')}</span>}
            </dd>

            <dt style={sty.verKey}>{t('version.status')}</dt>
            <dd style={sty.verVal}>
              {v.update_available
                ? <strong>{t('version.updateAvailable')}</strong>
                : t('version.upToDate')}
            </dd>

            {v.checked_at && (
              <>
                <dt style={sty.verKey}>{t('version.checkedAt')}</dt>
                <dd style={sty.verVal}>{new Date(v.checked_at).toLocaleString()}</dd>
              </>
            )}
          </>
        )}
      </dl>

      {v.check_enabled && (
        <p style={sty.verNote}>
          <button
            type="button"
            style={{ ...sty.btnGhost, ...(checkMutation.isPending ? sty.btnDisabled : {}) }}
            disabled={checkMutation.isPending}
            onClick={() => checkMutation.mutate()}
          >
            {checkMutation.isPending ? t('version.checking') : t('version.checkNow')}
          </button>
        </p>
      )}

      {!v.check_enabled && (
        <p style={sty.verNote}>
          {t('version.howToEnable')} <code style={sty.code}>FIREISP_UPDATE_CHECK=0</code>
          {' '}{t('version.howToEnableTail')}
        </p>
      )}

      {!v.running_sha && (
        <p style={sty.verNote}>{t('version.unknownBuildNote')}</p>
      )}

      {/* ── Deploy from here ────────────────────────────────────────────────
          The button only appears when a host agent has actually checked in.
          Without that check this would queue a request nobody services while
          the UI implied work was happening — a stub that fakes success. */}
      <hr style={sty.verRule} />
      <h3 style={sty.sectionTitle}>{t('deploy.title')}</h3>

      {!dep ? (
        <p style={sty.muted}>{t('version.loading')}</p>
      ) : !dep.agent_alive ? (
        <div>
          <p style={sty.verNote}>{t('deploy.agentPending')}</p>
          <p style={sty.verNote}>{t('deploy.agentPendingWhy')}</p>
        </div>
      ) : (
        <div>
          {/* Offered only when there is actually something to deploy. A button
              that redeploys the commit you are already on is at best a no-op
              and at worst a restart nobody asked for — and hiding it silently
              would read as broken, so the up-to-date case says so. */}
          {v.update_available ? (
            <button
              type="button"
              style={{ ...sty.btnPrimary, ...(busy || deployMutation.isPending ? sty.btnDisabled : {}) }}
              disabled={busy || deployMutation.isPending}
              onClick={() => deployMutation.mutate()}
            >
              {busy ? t('deploy.inProgress') : t('deploy.button')}
            </button>
          ) : (
            <p style={sty.muted}>
              {v.check_enabled ? t('deploy.nothingToDeploy') : t('deploy.enableCheckFirst')}
            </p>
          )}

          {deployError && <p style={sty.errorText}>{deployError}</p>}

          {dep.request && (
            <dl style={{ ...sty.verList, marginTop: '1rem' }}>
              <dt style={sty.verKey}>{t('deploy.lastStatus')}</dt>
              <dd style={sty.verVal}>{t(`deploy.status.${dep.request.status}`)}</dd>
              <dt style={sty.verKey}>{t('deploy.requestedAt')}</dt>
              <dd style={sty.verVal}>{new Date(dep.request.requested_at).toLocaleString()}</dd>
              {dep.request.finished_at && (
                <>
                  <dt style={sty.verKey}>{t('deploy.finishedAt')}</dt>
                  <dd style={sty.verVal}>{new Date(dep.request.finished_at).toLocaleString()}</dd>
                </>
              )}
            </dl>
          )}

          {dep.request?.output_tail && dep.request.status === 'failed' && (
            <pre style={sty.verPre}>{dep.request.output_tail}</pre>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Settings page
// ---------------------------------------------------------------------------

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'orgConfig', label: '🏢 Org Config' },
  { id: 'alertRules', label: '🚨 Alert Rules' },
  { id: 'paymentGateways', label: '💳 Payment Gateways' },
  { id: 'quotas', label: '📊 Quotas' },
  { id: 'emailSettings', label: '📧 Email' },
];

// Install-operator only. Appended rather than listed above so the array is
// unchanged for every other role — the endpoint behind it 404s a tenant admin,
// so showing them the tab would guarantee an error.
const VERSION_TAB: { id: SettingsTab; label: string } = { id: 'version', label: '⬆️ Version' };

export function Settings() {
  const [tab, setTab] = useState<SettingsTab>('orgConfig');
  const { user } = useAuth();
  // Resolved by the backend (GET /auth/me), not guessed from users.role: the
  // version of the software is a property of the INSTALL, and role === 'admin'
  // is the per-TENANT admin persona, so it would show this tab to every
  // tenant admin — whose /system/version calls answer 404.
  const isInstallOperator = user?.is_install_operator === true;
  const visibleTabs = isInstallOperator ? [...TABS, VERSION_TAB] : TABS;

  return (
    <div style={sty.page}>
      <h2 style={sty.pageTitle}>Settings</h2>

      {/* Tab bar */}
      <div style={sty.tabs}>
        {visibleTabs.map(t => (
          <button
            key={t.id}
            style={{ ...sty.tabBtn, ...(tab === t.id ? sty.tabBtnActive : {}) }}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={sty.card}>
        {tab === 'orgConfig' && <OrgConfigTab />}
        {tab === 'alertRules' && <AlertRulesTab />}
        {tab === 'paymentGateways' && <PaymentGatewaysTab />}
        {tab === 'quotas' && <QuotasTab />}
        {tab === 'emailSettings' && (
          <EmailSettingsTab userId={user?.id ?? null} organizationId={user?.organization_id ?? null} />
        )}
        {tab === 'version' && isInstallOperator && <VersionTab />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sty = {
  page: { padding: '1.5rem 2rem', fontFamily: 'var(--font-sans)', maxWidth: 1000 },
  pageTitle: { margin: '0 0 1rem', fontSize: '1.4rem' },
  tabs: { display: 'flex', gap: 4, borderBottom: '2px solid var(--border)', marginBottom: '1.25rem' },
  tabBtn: {
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--text-muted)',
    marginBottom: -2, transition: 'color .15s',
  } as React.CSSProperties,
  tabBtnActive: { color: 'var(--accent)', borderBottomColor: 'var(--accent)', fontWeight: 600 } as React.CSSProperties,
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '1.5rem' },
  sectionTitle: { margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 },
  tabBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  muted: { color: 'var(--text-faint)', fontStyle: 'italic' as const, fontSize: '0.875rem' },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.875rem' },
  th: { textAlign: 'left' as const, padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)', fontWeight: 600, color: 'var(--text-secondary)' },
  td: { padding: '0.6rem 0.75rem', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' as const },
  code: { background: 'var(--bg-subtle)', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.82rem' },
  valueCap: { display: 'block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  badge: { padding: '2px 8px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600, color: '#fff' },
  rowActions: { display: 'flex', gap: 6 },
  errorText: { color: '#dc2626', fontSize: '0.85rem', margin: '0.5rem 0 0' },
  hint: { fontWeight: 400, color: 'var(--text-faint)', fontSize: '0.8rem' },
  // modal
  overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80, zIndex: 1000 },
  modal: { background: 'var(--bg-card)', borderRadius: 8, width: '100%', maxWidth: 560, boxShadow: '0 8px 32px rgba(0,0,0,.2)', maxHeight: '80vh', overflow: 'auto' as const },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)' },
  modalBody: { padding: '1.25rem' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1rem' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--text-faint)' },
  form: { display: 'flex', flexDirection: 'column' as const, gap: 12 },
  label: { display: 'flex', flexDirection: 'column' as const, gap: 4, fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)' },
  input: { padding: '0.45rem 0.65rem', border: '1px solid var(--input-border)', borderRadius: 6, fontSize: '0.875rem', width: '100%', boxSizing: 'border-box' as const },
  select: { padding: '0.45rem 0.65rem', border: '1px solid var(--input-border)', borderRadius: 6, fontSize: '0.875rem', background: 'var(--input-bg)', width: '100%', boxSizing: 'border-box' as const },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  verList: { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.5rem 1.25rem', margin: 0, fontSize: '0.9rem' },
  verKey: { color: 'var(--text-secondary)', fontWeight: 500 },
  verVal: { margin: 0 },
  verNote: { marginTop: '1.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 },
  verRule: { border: 0, borderTop: '1px solid var(--border)', margin: '1.75rem 0 1.25rem' },
  verPre: { background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.75rem', fontSize: '0.78rem', overflowX: 'auto' as const, whiteSpace: 'pre' as const, margin: '0.75rem 0' },
  btnDisabled: { opacity: 0.55, cursor: 'not-allowed' as const },
  checkRow: { display: 'flex', gap: 24, fontSize: '0.875rem' },
  // buttons
  btnPrimary: { padding: '0.4rem 1rem', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 },
  btnGhost: { padding: '0.4rem 1rem', background: 'var(--bg-body)', color: 'var(--text-secondary)', border: '1px solid var(--border-strong)', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem' },
  btnDanger: { padding: '0.4rem 1rem', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem' },
};
